import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import { useGetCapabilitiesSalesTypesQuery, useUpdateCapabilitiesSalesTypeMutation } from "./capabilitiesSalesTypesApiSlice"

const EtUpdateCapabilitiesSalesType = ({id, setActiveAdminView}) => {
  // Created - 18 Nov 2024
  // Author - Atul Pandey
  // Updated - 27 Feb 2025 - updated the sales with props - { id, setActiveAdminView}
  // Updated - 27 Feb 2025 - switched from navigate to setActiveAdminView

  // 1 - Set Title
  useTitle("Update CapabilitiesSales Type")

  // 2 - extract the capabilitiesSalesType Id from the url
  // const { id } = useParams()
  // //console.log("id:", id)

  // 3 - Extract the capabilitiesSalesType from the query to load the capabilitiesSalesType data
  const { capabilitiesSalesType } = useGetCapabilitiesSalesTypesQuery("CapabilitiesSalesType", {
    selectFromResult: ({ data }) => ({
      capabilitiesSalesType: data?.entities[id],
    }),
  })

  //console.log('capabilitiesSales type:', capabilitiesSalesType)

  // 4 - Check for returned data
  if (!capabilitiesSalesType) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 5. Display data
  let content
  content = <EtUpdateCapabilitiesSalesTypeForm capabilitiesSalesType={capabilitiesSalesType} id={id} setActiveAdminView={setActiveAdminView}/> // 27 Feb 2025 - added setActiveAdminView={setActiveAdminView}
  return content
}

const EtUpdateCapabilitiesSalesTypeForm = ({ capabilitiesSalesType, id, setActiveAdminView}) =>{
  // 1 - set the variable for navigate
  const navigate = useNavigate()

  //console.log('update form invoked!')

  // 2 - set the mutation to create the new section data
  const[updateCapabilitiesSalesType, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useUpdateCapabilitiesSalesTypeMutation()

// 3 - set the form data object
const [formData, setFormData] = useState({
  capabilitiesSalesTypeName: capabilitiesSalesType.capabilitiesSalesTypeName,
  capabilitiesSalesTypeDesc: capabilitiesSalesType.capabilitiesSalesTypeDesc,
  aboutMe: capabilitiesSalesType.aboutMe,
})

//console.log('formData for update:', formData)

// 4 - Reset form upon successful submission
useEffect(() => {
  if(isSuccess) {
    setFormData({
      capabilitiesSalesTypeName: "",
      capabilitiesSalesTypeDesc: "",
      aboutMe: "",
      pic: "",
    })

    // navigate('/capabilitiesSalestypes')
    setActiveAdminView('capabilitiesSalesTypes'); // 27 Feb 2025
  }

},[isSuccess]) // ✅ 27 Feb 2025 - Removed `navigate` since it's no longer used.

// 5 - Form field updates / change handling

const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
}

// 6 - sales to strip html tags from sections
const stripHtml = (html) => {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
}

// 7 - Form Submit Logic
const handleSubmit = async (e) => {
e.preventDefault()

// Initialize an object to collect the form data
const capabilitiesSalesTypeData = {
  capabilitiesSalesTypeName: formData.capabilitiesSalesTypeName,
  capabilitiesSalesTypeDesc: formData.capabilitiesSalesTypeDesc,
  aboutMe: formData.aboutMe,
}

//console.log('capabilitiesSalesType Name:',capabilitiesSalesTypeData.capabilitiesSalesTypeName)

if (formData.pic) {
  // Convert the image to a base64 string
  const reader = new FileReader()
  reader.onloadend = async () => {
    // Once conversion is done, add the base64 string to your data object
    capabilitiesSalesTypeData.pic = reader.result

    // Now, send the data object to the server
    try {
      capabilitiesSalesTypeData.id = id
      console.log("Form submitted.")
      await updateCapabilitiesSalesType(capabilitiesSalesTypeData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
  reader.readAsDataURL(formData.pic)
} else {
  // No image, send other data as is
  try {
    capabilitiesSalesTypeData.id = id
    console.log("Form submitted.")
    await updateCapabilitiesSalesType(capabilitiesSalesTypeData).unwrap() // Dispatch the RTK Query mutation
  } catch (error) {
    console.error(error)
  }
}
}

// 8 - Handle Cancel

const handleCancel = () => {
  // navigate("/capabilitiesSalestypes")
  setActiveAdminView('capabilitiesSalesTypes'); // 27 Feb 2025
}

// 9 - Handle Clear
const handleClear = () => {
  setFormData({
    capabilitiesSalesTypeName: "",
    capabilitiesSalesTypeDesc: "",
    aboutMe: "",
    pic: "",
    })
}

return (
  <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">
    {isLoading && <PulseLoader color={"#ea580c"} />}
    {isError && (
      <p className="text-orange-700 font-semi-bold py-2">
        {error?.data?.message}
      </p>
    )}
    <div className="bg-white p-1 m-1 rounded">
      <p className="p-1 m-1 font-bold font-poppins text-xl">Update a CapabilitiesSales Type</p>

      <div className="bg-gray-200 rounded m-1 p-1">
        <form
          onSubmit={handleSubmit}
          className="m-1 p-1 text-xs font-poppins"
        >
          
          <div id="capabilitiesSalesTypeName">
            <input
              type="text"
              name="capabilitiesSalesTypeName"
              placeholder="Enter the capabilitiesSales type name here"
              value={formData.capabilitiesSalesTypeName}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div className="bg-white w-full rounded p-2 m-1">
            <EtContentEditorLightWithPlace
              value={formData.capabilitiesSalesTypeDesc}
              handleChange={(content) =>
                setFormData({ ...formData, capabilitiesSalesTypeDesc: content })
              }
              placeholderText={
                "Write this capabilitiesSales type is about or will be used for"
              }
              // toolbarId={'sectionDesc'}
            />
          </div>

          <div id="aboutMe">
            <input
              type="text"
              name="aboutMe"
              placeholder="Add something about the personality about this capabilitiesSales type"
              value={formData.aboutMe}
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

          <div id="pic">
            <input
              type="file"
              name="pic"
              onChange={handleChange}
              className="w-full rounded p-2 m-1"
            />
          </div>

         <div id="capabilitiesSaless" className="bg-white m-1 px-1 py-3 rounded">
            <EtBtnFormBasic
              name={"Update"}
              type={"create"}
              handleClick={handleSubmit}
            />
            <EtBtnFormBasic
              name={"Clear"}
              type={"clear"}
              handleClick={handleClear}
            />
            <EtBtnFormBasic
              name={"Cancel"}
              type={"cancel"}
              handleClick={handleCancel}
            />
          </div>
        </form>
      </div>
    </div>
  </div>
)

}

export default EtUpdateCapabilitiesSalesType